import React, { useMemo } from "react";
import { ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { useHedgefunds } from "../../hooks/useHedgefunds";
import { useTheme } from "@mui/material";
import clsx from "clsx";
import styles from "./Hedgefunds.module.scss";
import { useTranslation } from "next-i18next";

const GAUGE_CX = 105;
const GAUGE_CY = 100;
const GAUGE_INNER_R = 88;
const GAUGE_OUTER_R = 100;
const NEEDLE_BASE_R = 5;
const NEEDLE_LEN = 72;

const GAUGE_SECTORS = [
    { value: 40, color: "#F25555" },
    { value: 40, color: "#F25555" },
    { value: 40, color: "url(#segmentGradient)" },
    { value: 40, color: "#16BB6F" },
    { value: 40, color: "#16BB6F" },
];

const calcAngle = (buy: number, hold: number, sell: number) => {
    const total = buy + hold + sell;
    if (total === 0) return 90;
    const index = (buy - sell) / total; // -1..+1
    const deg = ((index + 1) / 2) * 180; // 0..180
    return 180 - Math.round(deg); // переворот для transform: rotate(-angle)
};

type GaugeLabelKey = "strongBuy" | "buy" | "neutral" | "sell" | "strongSell";

const getLabelKey = (buy: number, hold: number, sell: number): GaugeLabelKey => {
    if (buy === 0 && sell === 0) return "neutral";
    if (buy >= sell * 1.75) return "strongBuy";
    if (buy > sell) return "buy";
    if (sell >= buy * 1.75) return "strongSell";
    if (sell > buy) return "sell";
    return "neutral";
};

const Needle = ({
    cx,
    cy,
    angle,
    theme,
}: {
    cx: number;
    cy: number;
    angle: number;
    theme: "dark" | "light" | string;
}) => {
    const color = theme === "dark" ? "#d9d9d9" : "#343841";
    return (
        <g>
            <circle cx={cx} cy={cy} r={NEEDLE_BASE_R} fill={color} />
            <path
                d={`M${cx},${cy} l${NEEDLE_LEN},0`}
                strokeWidth={2}
                stroke={color}
                fill={color}
                style={{ transform: `rotate(-${angle}deg)`, transformOrigin: `${cx}px ${cy}px` }}
            />
        </g>
    );
};

export const Hedgefunds: React.FC<{ ticker: string }> = ({ ticker }) => {
    const { data, isLoading, error } = useHedgefunds({ ticker });
    const { palette } = useTheme();
    const { t } = useTranslation("common");

    const { totalBuy, totalSell, totalHold } = useMemo(() => {
        const holdings = data?.holdingData?.holdings || [];
        return {
            totalBuy: holdings.reduce((s, h) => s + (h.numberOfSharesBought || 0), 0),
            totalSell: holdings.reduce((s, h) => s + (h.numberOfSharesSold || 0), 0),
            totalHold: holdings.reduce((s, h) => s + (h.sharesHeld || 0), 0),
        };
    }, [data]);

    if (isLoading || error || !data?.holdingData?.holdings) return null;

    const edgeLabel = (labelKey: GaugeLabelKey) => {
        const text = t(`analytics.analystForecasts.gauges.labels.${labelKey}`);
        const parts = text.split("\n");
        return parts.map((part, idx) => (
            <React.Fragment key={`${part}-${idx}`}>
                {part}
                {idx < parts.length - 1 && <br />}
            </React.Fragment>
        ));
    };

    const angle = calcAngle(totalBuy, totalHold, totalSell);
    const labelKey = getLabelKey(totalBuy, totalHold, totalSell);
    const labelText = t(`analytics.analystForecasts.gauges.labels.${labelKey}`).replace(/\n/g, " ");

    return (
        <div className={styles.container}>
            <div className={styles.title}>
                {t("analytics.analystForecasts.hedgeFunds.title", { ticker })}
            </div>

            <div className={styles.chart}>
                <ResponsiveContainer width="100%" height={225}>
                    <PieChart>
                        <defs>
                            <linearGradient id="segmentGradient">
                                <stop offset="0%" stopColor="#F25555" />
                                <stop offset="100%" stopColor="#16BB6F" />
                            </linearGradient>
                        </defs>

                        {/* дуги полукруга */}
                        <Pie
                            data={GAUGE_SECTORS}
                            dataKey="value"
                            startAngle={180}
                            endAngle={0}
                            cx={GAUGE_CX}
                            cy={GAUGE_CY}
                            innerRadius={GAUGE_INNER_R}
                            outerRadius={GAUGE_OUTER_R}
                            stroke="none"
                            paddingAngle={4}>
                            {GAUGE_SECTORS.map((s, i) => (
                                <Cell key={i} fill={s.color} />
                            ))}
                        </Pie>

                        {/* оверлей, где через activeShape рисуем стрелку */}
                        <Pie
                            data={GAUGE_SECTORS}
                            dataKey="value" // ← ОБЯЗАТЕЛЬНО
                            startAngle={180}
                            endAngle={0}
                            cx={GAUGE_CX}
                            cy={GAUGE_CY}
                            innerRadius={GAUGE_INNER_R}
                            outerRadius={GAUGE_OUTER_R}
                            stroke="none"
                            fill="none"
                            activeIndex={1} // можно любой валидный индекс
                            activeShape={() => (
                                <Needle cx={GAUGE_CX} cy={GAUGE_CY} angle={angle} theme={palette.mode} />
                            )}
                            isAnimationActive={false}
                        />
                    </PieChart>
                </ResponsiveContainer>

                {/* подписи под полукругом — как в твоих стилях */}
                <div className={styles.indicators}>
                    <span className={styles.chartLabel}>{edgeLabel("strongSell")}</span>

                    <span
                        className={clsx(styles.needleLabel, {
                            [styles.sell]: labelKey === "sell" || labelKey === "strongSell",
                            [styles.neutral]: labelKey === "neutral",
                        })}>
                        {labelText}
                    </span>

                    <span className={styles.chartLabel}>{edgeLabel("strongBuy")}</span>
                </div>
            </div>

            {/* легенда снизу */}
            <div className={styles.scoreInfo}>
                <div className={styles.item}>
                    <div className={styles.red}></div>
                    <div className={styles.label}>
                        {t("analytics.analystForecasts.hedgeFunds.sold")}{" "}
                        <span>{totalSell.toLocaleString()}</span>
                    </div>
                </div>
                <div className={styles.item}>
                    <div className={styles.yellow}></div>
                    <div className={styles.label}>
                        {t("analytics.analystForecasts.hedgeFunds.held")}{" "}
                        <span>{totalHold.toLocaleString()}</span>
                    </div>
                </div>
                <div className={styles.item}>
                    <div className={styles.green}></div>
                    <div className={styles.label}>
                        {t("analytics.analystForecasts.hedgeFunds.bought")}{" "}
                        <span>{totalBuy.toLocaleString()}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
